// ─────────────────────────────────────────────────────────────────────────────
// localStorage slot storage for build saves (the source of truth; the cloud
// mirrors it). Each slot key `saveSlot{i}` holds a v1 payload plus slot metadata:
//   { v, name, updatedAt, stats, job, baseLevel, jobLevel, inventory, skills }
// ─────────────────────────────────────────────────────────────────────────────

import * as JobName from '../data/jobID'
import { serializeBuild, migrateLegacySlot, isVersioned } from './buildSerializer'

export const MAX_SAVE_SLOTS = 20

export const slotKey = (i) => 'saveSlot' + i
const backupKey = (i) => 'saveSlot_backup_' + i

export function readSlot(i) {
	const raw = window.localStorage.getItem(slotKey(i))
	if (!raw) return null
	try {
		return JSON.parse(raw)
	} catch {
		return null
	}
}

export function writeSlot(i, slot) {
	window.localStorage.setItem(slotKey(i), JSON.stringify(slot))
}

export function emptySlot() {
	return { ...serializeBuild({}), name: '', updatedAt: null }
}

// Ensure all 20 slots exist in the v1 format. Legacy slots (old Character JSON,
// no `v`) are backed up once then migrated, so no existing build is lost.
export function ensureSlots() {
	for (let i = 0; i < MAX_SAVE_SLOTS; i++) {
		const raw = window.localStorage.getItem(slotKey(i))
		if (!raw) {
			writeSlot(i, emptySlot())
			continue
		}
		let parsed
		try {
			parsed = JSON.parse(raw)
		} catch {
			writeSlot(i, emptySlot())
			continue
		}
		if (isVersioned(parsed)) continue
		if (!window.localStorage.getItem(backupKey(i))) {
			window.localStorage.setItem(backupKey(i), raw)
		}
		const payload = migrateLegacySlot(parsed)
		const name = payload.job ? JobName.getClassName(payload.job) : ''
		writeSlot(i, { ...payload, name, updatedAt: null })
	}
}

export function slotLabel(i, slot) {
	const name = slot?.name?.trim()
	if (name) return `${i + 1}. ${name}`
	const job = slot?.job ? JobName.getClassName(slot.job) : 'Empty'
	return `${i + 1}. ${job}`
}

export function buildOptions() {
	const opts = []
	for (let i = 0; i < MAX_SAVE_SLOTS; i++) {
		const slot = readSlot(i)
		opts.push({ value: i, label: slotLabel(i, slot), key: slotKey(i) })
	}
	return opts
}

// ─── Cloud-sync helpers ───────────────────────────────────────────────────────

// Build data without slot metadata (name / updatedAt). This is the payload
// stored in the cloud `payload` column.
export function stripMeta(slot) {
	if (!slot || typeof slot !== 'object') return {}
	// eslint-disable-next-line no-unused-vars
	const { name, updatedAt, ...payload } = slot
	return payload
}

// A slot is considered non-empty (worth syncing) if it has a name, a job, any
// equipped item, or any skill.
export function slotHasData(slot) {
	if (!slot) return false
	if (slot.name && slot.name.trim()) return true
	const p = stripMeta(slot)
	if (p.job && p.job !== 0) return true
	if (p.inventory && Object.keys(p.inventory).length > 0) return true
	if (p.skills && p.skills.length > 0) return true
	return false
}

// Stable comparison key for divergence detection (name + build data, no dates).
export function slotComparable(nameAndPayload) {
	return JSON.stringify({
		name: (nameAndPayload.name || '').trim(),
		payload: nameAndPayload.payload,
	})
}

// Convert a cloud row { slot, name, payload, updatedAt } into a local slot.
export function cloudToSlot(row) {
	return { ...row.payload, name: row.name || '', updatedAt: row.updatedAt || null }
}

export function readAllSlots() {
	const slots = []
	for (let i = 0; i < MAX_SAVE_SLOTS; i++) slots.push(readSlot(i))
	return slots
}
